import React, { useEffect, useState, useRef } from 'react';
type Metric = {
  value?: number;
  prefix?: string;
  suffix?: string;
  display?: string;
  label: string;
  source: string;
};
const metrics: Metric[] = [{
  value: 98,
  suffix: '%',
  label: 'Customer satisfaction',
  source: 'U.S. Bank One Card'
}, {
  value: 85,
  suffix: '%',
  label: 'AI CV parsing accuracy',
  source: 'Case Western'
}, {
  value: 65,
  suffix: '%',
  label: 'Faster claim handling',
  source: 'U.S. Bank'
}, {
  display: '$1M+',
  label: 'AI funding unlocked',
  source: 'Agency innovation lab'
}];
export function MetricsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [counts, setCounts] = useState(metrics.map(() => 0));
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.25
    };
    const observer = new IntersectionObserver(entries => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, observerOptions);
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  useEffect(() => {
    if (!isVisible) return;
    const duration = 1800;
    const frameDuration = 1000 / 60;
    const totalFrames = Math.round(duration / frameDuration);
    const counters = metrics.map((metric, index) => {
      // Metrics with a static display string don't animate.
      if (metric.display || typeof metric.value !== 'number') return null;
      const finalValue = metric.value;
      const increment = finalValue / totalFrames;
      let frame = 0;
      return setInterval(() => {
        frame++;
        const value = Math.min(Math.round(increment * frame), finalValue);
        setCounts(prev => {
          const newCounts = [...prev];
          newCounts[index] = value;
          return newCounts;
        });
        if (frame >= totalFrames) {
          const c = counters[index];
          if (c) clearInterval(c);
        }
      }, frameDuration);
    });
    return () => {
      counters.forEach(counter => counter && clearInterval(counter));
    };
  }, [isVisible]);
  return <section className="bg-white py-24 sm:py-32 md:py-40 px-6 sm:px-12 md:px-16 lg:px-24 border-t border-gray-100" data-id="element-1727">
      <div className="container mx-auto max-w-7xl" data-id="element-1728">
        <div ref={sectionRef} className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 sm:gap-8 md:gap-12" data-id="element-1729">
          {metrics.map((metric, index) => <div key={index} className="text-center" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
          transition: `opacity 0.7s ease-out ${index * 0.1}s, transform 0.7s ease-out ${index * 0.1}s`
        }} data-id="element-1730">
              <div className="text-4xl sm:text-5xl md:text-6xl font-light mb-2 sm:mb-3 text-black tabular-nums" style={{
            fontFamily: "'Gefika', sans-serif"
          }} data-id="element-1731">
                {metric.display ? metric.display : `${metric.prefix ?? ''}${counts[index]}${metric.suffix ?? ''}`}
              </div>
              <div className="text-xs sm:text-sm tracking-widest text-gray-700 font-medium uppercase mb-1.5" style={{
            fontFamily: "'Ronzino', sans-serif"
          }} data-id="element-1732">
                {metric.label}
              </div>
              <div className="text-[10px] sm:text-xs tracking-[0.15em] text-gray-400 uppercase" style={{
            fontFamily: "'Ronzino', sans-serif"
          }} data-id="element-1733">
                {metric.source}
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}